import React, { useMemo } from "react";

const GST_RATE = 0.18;

const toNumberOrNull = (value) => {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const formatCurrency = (value) => {
  const n = toNumberOrNull(value);
  if (n == null) return "—";
  return `₹${n.toLocaleString("en-IN", {
    minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
};

/** Incl.-GST delivery line, or bold green FREE when charge is zero. */
export function formatDeliveryChargesSummaryDisplay(
  deliveryCharge,
  deliveryGst,
  deliveryTotal,
  formatMoney = formatCurrency
) {
  const total = toNumberOrNull(deliveryTotal) ?? 0;
  const exTax = toNumberOrNull(deliveryCharge) ?? 0;
  if (Math.round(total) === 0 && Math.round(exTax) === 0) {
    return <span className="fw-bold text-success">FREE</span>;
  }
  return `${formatMoney(total)} (${formatMoney(exTax)} + GST)`;
}

/** Single delivery amount (incl. GST), or bold green FREE when zero. */
export function formatDeliveryChargeAmount(value, formatMoney = formatCurrency) {
  const n = toNumberOrNull(value);
  if (n == null) return "—";
  if (Math.round(n) === 0) {
    return <span className="fw-bold text-success">FREE</span>;
  }
  return formatMoney(n);
}

function SummaryRow({ label, value, bold = false, className = "" }) {
  return (
    <div className={`d-flex justify-content-between py-1 ${className}`.trim()}>
      <span className={bold ? "fw-semibold" : "text-muted"}>{label}</span>
      <span className={bold ? "fw-bold" : undefined}>{value}</span>
    </div>
  );
}

function couponSummaryLabel(coupon) {
  const code = coupon?.code || "";
  const label = `Coupon Discount (${code})`;
  return coupon?.auto_applied === true ? `${label} (auto)` : label;
}

/**
 * Order Summary / review breakdown from API +price_meta+ (direct mode).
 */
export default function OrderSummaryPriceMetaRows({
  meta,
  deliveryChargesBracketLabel = null,
}) {
  const rows = useMemo(() => {
    if (!meta || typeof meta !== "object") return null;

    const discounts = Array.isArray(meta.discounts) ? meta.discounts : [];
    const baseDiscount = discounts.find((d) => d.type === "base");
    const onboardingDiscount = discounts.find((d) => d.type === "onboarding");
    const couponDiscounts = discounts.filter((d) => d.type === "coupon");
    const valueCoupons = couponDiscounts.filter(
      (coupon) => coupon.discount_level !== "delivery"
    );
    const deliveryCoupons = couponDiscounts.filter(
      (coupon) => coupon.discount_level === "delivery"
    );

    const baseValue = toNumberOrNull(meta?.base_value);
    const creditNotes = toNumberOrNull(meta?.used_credit_notes) ?? 0;
    const taxableValue = toNumberOrNull(meta?.total_after_credit_notes);
    const gst = toNumberOrNull(meta?.gst);
    const totalAfterGst = toNumberOrNull(meta?.total_after_gst);
    const deliveryCharge = toNumberOrNull(meta?.delivery_charge) ?? 0;
    const deliveryGst = toNumberOrNull(meta?.delivery_gst) ?? 0;
    const deliveryTotal = deliveryCharge + deliveryGst;
    const payable = toNumberOrNull(meta?.total);

    const itemsInclGstBeforeValueCoupons =
      taxableValue != null && gst != null ? taxableValue + gst : null;

    const deliveryDiscountIncl = deliveryCoupons.reduce(
      (sum, coupon) => sum + (toNumberOrNull(coupon.discount_value) ?? 0),
      0
    );
    const deliveryInclBeforeCoupons =
      deliveryDiscountIncl > 0 ? deliveryTotal + deliveryDiscountIncl : deliveryTotal;

    return {
      baseValue,
      baseDiscount,
      onboardingDiscount,
      valueCoupons,
      deliveryCoupons,
      creditNotes,
      taxableValue,
      gst,
      itemsInclGstBeforeValueCoupons,
      totalAfterGst,
      deliveryCharge,
      deliveryGst,
      deliveryTotal,
      deliveryInclBeforeCoupons,
      payable,
    };
  }, [meta]);

  if (!rows) {
    return <p className="text-muted mb-0">No price summary available.</p>;
  }

  const {
    baseValue,
    baseDiscount,
    onboardingDiscount,
    valueCoupons,
    deliveryCoupons,
    creditNotes,
    taxableValue,
    gst,
    itemsInclGstBeforeValueCoupons,
    totalAfterGst,
    deliveryCharge,
    deliveryGst,
    deliveryTotal,
    deliveryInclBeforeCoupons,
    payable,
  } = rows;

  const deliveryLabelSuffix = deliveryChargesBracketLabel
    ? ` ${deliveryChargesBracketLabel}`
    : "";

  const hasDeliveryCoupons = deliveryCoupons.length > 0;

  return (
    <div>
      <SummaryRow label="Item Total" value={formatCurrency(baseValue)} />
      {baseDiscount ? (
        <>
          <SummaryRow
            label="Base discount"
            value={`-${formatCurrency(baseDiscount?.discount_value)} (${baseDiscount?.percentage}% of ${formatCurrency(baseDiscount?.value)})`}
          />
          <SummaryRow
            label="Sub-total"
            value={formatCurrency(baseDiscount?.effective_value)}
          />
        </>
      ) : null}
      {onboardingDiscount ? (
        <>
          <SummaryRow
            label="Onboarding discount"
            value={`-${formatCurrency(onboardingDiscount.discount_value)} (${onboardingDiscount.percentage}% of ${formatCurrency(onboardingDiscount.value)})`}
          />
          <SummaryRow
            label="Sub-total"
            value={formatCurrency(onboardingDiscount.effective_value)}
          />
        </>
      ) : null}
      <SummaryRow
        label="Credit Notes"
        value={`-${formatCurrency(creditNotes)} (excl. GST)`}
      />
      <SummaryRow label="Taxable Value" value={formatCurrency(taxableValue)} />
      <SummaryRow label="GST" value={formatCurrency(gst)} />
      {valueCoupons.length > 0 && itemsInclGstBeforeValueCoupons != null ? (
        <SummaryRow
          label="Items (incl. GST)"
          value={formatCurrency(itemsInclGstBeforeValueCoupons)}
        />
      ) : null}
      {valueCoupons.map((coupon) => (
        <SummaryRow
          key={`${coupon.code}-value`}
          label={couponSummaryLabel(coupon)}
          value={`-${formatCurrency(coupon.discount_value)}`}
        />
      ))}
      <SummaryRow
        label="Total (incl. GST)"
        value={formatCurrency(totalAfterGst)}
        bold
        className="border-top mt-1 pt-2"
      />
      <SummaryRow
        label={`Delivery Charges${deliveryLabelSuffix}`}
        value={
          hasDeliveryCoupons
            ? formatDeliveryChargesSummaryDisplay(
                Math.round(deliveryInclBeforeCoupons / (1 + GST_RATE)),
                deliveryInclBeforeCoupons -
                  Math.round(deliveryInclBeforeCoupons / (1 + GST_RATE)),
                deliveryInclBeforeCoupons
              )
            : formatDeliveryChargesSummaryDisplay(
                deliveryCharge,
                deliveryGst,
                deliveryTotal
              )
        }
      />
      <div className="text-muted small pb-1">(Unloading not included)</div>
      {deliveryCoupons.map((coupon) => (
        <SummaryRow
          key={`${coupon.code}-delivery`}
          label={couponSummaryLabel(coupon)}
          value={`-${formatCurrency(coupon.discount_value)}`}
        />
      ))}
      {hasDeliveryCoupons ? (
        <SummaryRow
          label="Delivery total (incl. GST)"
          value={formatDeliveryChargeAmount(deliveryTotal)}
        />
      ) : null}
      <SummaryRow
        label="Payable"
        value={formatCurrency(payable)}
        bold
        className="border-top mt-1 pt-2"
      />
    </div>
  );
}

export function payableTotalFromPriceMeta(meta) {
  if (!meta || typeof meta !== "object") return null;
  const n = Number(meta.total);
  return Number.isFinite(n) ? n : null;
}

/** Items incl. GST for header chip — pre-credit when credits zero out +total_after_gst+. */
export function headerItemsTotalFromPriceMeta(meta, gstRate = 0.18) {
  if (!meta || typeof meta !== "object") return null;
  const afterGst = Number(meta.total_after_gst);
  if (Number.isFinite(afterGst) && afterGst > 0) return afterGst;

  const afterDiscount = Number(meta.total_after_discount);
  if (Number.isFinite(afterDiscount) && afterDiscount >= 0) {
    return Math.round(afterDiscount * (1 + gstRate));
  }

  const base = Number(meta.base_value);
  if (Number.isFinite(base) && base >= 0) {
    return Math.round(base * (1 + gstRate));
  }

  return Number.isFinite(afterGst) ? afterGst : null;
}
